import type { FeatureReport } from "@asurion-core/expert-workspace-feature-flagging";

import { reactAnalytics } from "@asurion-core/eventualize-react";
import { setFeatureReporter } from "@asurion-core/expert-workspace-feature-flagging";
import { getLogger } from "@expert/logging";

import type { RootDispatcherType } from "./rootDispatcher";

const logger = getLogger({ module: "Analytics" });

/** `appName` is omitted because the dispatcher overwrites it; `source` tells expert-ui and expert-assist apart. */
const CONTEXT_FIELDS = ["partner", "channel", "agentSite", "environment", "source"] as const;

/** The context fields a value carries; a field the call site did not pass is left out. */
type ContextFields = Partial<Record<(typeof CONTEXT_FIELDS)[number], string>>;

/** One flag value as it is sent to analytics, one event per value. */
export type FeatureFlagValue = ContextFields & {
    featureKey: string;
    status: "ok" | "notConfigured" | "failed";
    /** The value as a string when `status` is `ok`, otherwise empty, so a missing config never reads as the caller's default. */
    value: string;
    /** `typeof` the value when `status` is `ok` (`"null"` for null), otherwise empty. */
    valueType: string;
};

/**
 * Flags not worth reporting. `EWP-isTestUser` only decides whether this user's events skip EDP, so every value that
 * could reach EDP is `false`. It is also the only flag read before `setIdentityAsTestUser` runs; every other read
 * happens inside the workspace `AuthHandler` renders after it, so SkipEDP applies to them.
 */
const NOT_REPORTED = new Set(["EWP-isTestUser"]);

/** Long values (in practice JSON objects) are cut to this length; enough to tell them apart without bloating the event. */
const MAX_VALUE_LENGTH = 1000;

/** The values already reported in this page load; see {@link reportFeatureValue}. */
const reported = new Set<string>();

/**
 * Values read before the dispatcher has the expert's partner, or undefined once it has. Held rather than sent under
 * the dispatcher's start-up placeholder partner.
 */
let held: FeatureFlagValue[] | undefined = [];

/** The last send queued; each send waits for it, so only one request is in flight at a time. */
let sending: Promise<void> = Promise.resolve();

function contextFieldsOf(context: Record<string, unknown>): ContextFields {
    const fields: ContextFields = {};
    for (const field of CONTEXT_FIELDS) {
        const value = context[field];
        if (typeof value === "string") fields[field] = value;
    }
    return fields;
}

function serializeValue(value: unknown): string {
    return typeof value === "object" && value !== null ? JSON.stringify(value) : String(value);
}

/** Cuts to {@link MAX_VALUE_LENGTH} without splitting a surrogate pair, which would leave an invalid character. */
function cutValue(text: string): string {
    if (text.length <= MAX_VALUE_LENGTH) return text;
    const cut = text.slice(0, MAX_VALUE_LENGTH);
    return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
}

/**
 * Queues a value to be sent after the ones before it. The values held through start-up are released together, so
 * sending them one at a time keeps them from competing with the workspace's own start-up requests.
 */
function send(value: FeatureFlagValue) {
    // `getRootDispatcher` returns this; read directly because `rootDispatcher` imports this module for the release.
    // Read now rather than when the send runs, so a queued value keeps the partner it was read under.
    const dispatcher: RootDispatcherType = reactAnalytics.dispatcher;
    sending = sending
        .then(() => dispatcher.dispatchBusinessEvent("FeatureFlagValue", value))
        .catch((error: unknown) => {
            // Without this, one rejected send would stop every send queued after it.
            logger.warn({ error, featureKey: value.featureKey }, "Unable to send a feature flag value");
        });
}

/**
 * Sends a `fetchFeature` result as a `FeatureFlagValue` event, the first time this page load sees it.
 *
 * De-duplicated on flag, status, value, partner, and channel, so repeat reads, focus refetches, and cache hits are
 * dropped while a changed value or partner is always recorded. Anything thrown here is caught and logged by
 * `fetchFeature`, so a flag read never sees it.
 */
function reportFeatureValue({ featureKey, result, context }: FeatureReport) {
    if (NOT_REPORTED.has(featureKey)) return;

    const status = result.ok ? "ok" : result.reason;
    const fields = contextFieldsOf(context);
    // `null` gets its own type rather than `"object"`, so a null flag never reads as an object one.
    const [serialized, valueType] = result.ok
        ? [serializeValue(result.value), result.value === null ? "null" : typeof result.value]
        : ["", ""];

    // The JSON array ends where it ends, so the value appended after it cannot run into a field, and an absent
    // partner (null) stays distinct from an empty one (""). Appending saves re-escaping a JSON value inside the key.
    const key = JSON.stringify([featureKey, status, valueType, fields.partner, fields.channel]) + serialized;
    if (reported.has(key)) return;
    reported.add(key);

    const value: FeatureFlagValue = { featureKey, status, value: cutValue(serialized), valueType, ...fields };
    if (held) held.push(value);
    else send(value);
}

/**
 * Sends every flag value `fetchFeature` returns to EDP, one `FeatureFlagValue` event per value, alongside the
 * fixed-list `FeatureFlagResults`.
 *
 * Values are held until the expert's partner is known, so none goes out under the dispatcher's start-up placeholder.
 * A page that never learns the partner (the agent SDK never loads) sends none, as it has no expert work to report.
 * Values are sent one at a time; any still waiting when the tab closes are lost.
 */
export function registerFeatureFlagReporter() {
    setFeatureReporter(reportFeatureValue);
}

/** Sends what was held, and every later value straight away. Call once the dispatcher has the expert's real partner. */
export function releaseFeatureFlagReports() {
    const values = held;
    held = undefined;
    values?.forEach(send);
}
